/**
 * Flow renderer — SVG edges + positioned nodes + hover popover.
 *
 * Takes a list of FlowStep nodes, computes the layout, and renders the
 * complete flowchart: an SVG edge layer beneath absolutely-positioned
 * FlowNode components, with a StepPopover on hover.
 */

import { useState, useCallback, useRef } from "react";
import { computeFlowLayout } from "./flow-layout";
import { FlowNode } from "./flow-nodes";
import { StepPopover } from "./step-popover";
import type { FlowStep, FlowEdge, NodePosition } from "./types";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface FlowRendererProps {
  steps: FlowStep[];
  onApprove?: (stepId: string) => void;
  onReject?: (stepId: string) => void;
  onClick?: (stepId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONTAINER_PADDING = 16;
/** Delay before clearing hover so the user can move to the popover. */
const HOVER_LEAVE_DELAY_MS = 150;

// ---------------------------------------------------------------------------
// Edge color helpers
// ---------------------------------------------------------------------------

const EDGE_COLOR = "var(--color-border)";

// ---------------------------------------------------------------------------
// SVG edge path builder — smooth bezier curves
// ---------------------------------------------------------------------------

/**
 * Build an SVG path connecting the bottom-center of the source node to the
 * top-center of the target node. Uses a smooth cubic bezier for curves,
 * with short straight segments at both ends so arrows always point straight
 * down into the target node.
 */
function buildEdgePath(from: NodePosition, to: NodePosition): string {
  const sourceX = from.x + from.width / 2;
  const sourceBottomY = from.y + from.height;
  const targetX = to.x + to.width / 2;
  const targetTopY = to.y;

  // Straight line when same column
  if (Math.abs(sourceX - targetX) < 1) {
    return `M ${sourceX},${sourceBottomY} L ${sourceX},${targetTopY}`;
  }

  // Short straight segments (12px) at start/end ensure the arrow and
  // departure are always perfectly vertical, regardless of curve angle.
  const straightLen = 12;
  const startY = sourceBottomY + straightLen;
  const endY = targetTopY - straightLen;
  const midY = (startY + endY) / 2;

  return [
    `M ${sourceX},${sourceBottomY}`,
    `L ${sourceX},${startY}`,
    `C ${sourceX},${midY} ${targetX},${midY} ${targetX},${endY}`,
    `L ${targetX},${targetTopY}`,
  ].join(" ");
}

// ---------------------------------------------------------------------------
// Arrow markers
// ---------------------------------------------------------------------------

const ARROW_MARKER_ID = "flow-arrow";

// ---------------------------------------------------------------------------
// Edge label position
// ---------------------------------------------------------------------------

function edgeLabelPosition(
  from: NodePosition,
  to: NodePosition,
): { x: number; y: number } {
  const sourceX = from.x + from.width / 2;
  const sourceBottomY = from.y + from.height;
  const targetX = to.x + to.width / 2;
  const targetTopY = to.y;
  const midY = (sourceBottomY + targetTopY) / 2;

  return {
    x: (sourceX + targetX) / 2,
    y: midY,
  };
}

// ---------------------------------------------------------------------------
// SVG edges layer
// ---------------------------------------------------------------------------

interface EdgeLayerProps {
  edges: FlowEdge[];
  positions: NodePosition[];
  width: number;
  height: number;
}

function EdgeLayer({ edges, positions, width, height }: EdgeLayerProps) {
  const positionMap = new Map<string, NodePosition>();
  for (const pos of positions) {
    positionMap.set(pos.stepId, pos);
  }

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width={width}
      height={height}
      data-testid="flow-edge-layer"
    >
      <defs>
        <marker
          id={ARROW_MARKER_ID}
          viewBox="0 0 6 6"
          refX="6"
          refY="3"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M 0 0 L 6 3 L 0 6 Z" fill={EDGE_COLOR} />
        </marker>
      </defs>

      {edges.map((edge) => {
        const fromPos = positionMap.get(edge.fromStepId);
        const toPos = positionMap.get(edge.toStepId);
        if (!fromPos || !toPos) return null;

        const pathD = buildEdgePath(fromPos, toPos);
        const edgeKey = `${edge.fromStepId}-${edge.toStepId}`;

        return (
          <g key={edgeKey}>
            <path
              d={pathD}
              fill="none"
              stroke={EDGE_COLOR}
              strokeWidth={1.5}
              markerEnd={`url(#${ARROW_MARKER_ID})`}
              data-testid={`flow-edge-${edgeKey}`}
            />
            {edge.label && (
              <EdgeLabel label={edge.label} from={fromPos} to={toPos} />
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Edge label component
// ---------------------------------------------------------------------------

interface EdgeLabelProps {
  label: string;
  from: NodePosition;
  to: NodePosition;
}

function EdgeLabel({ label, from, to }: EdgeLabelProps) {
  const { x, y } = edgeLabelPosition(from, to);
  const textPadX = 5;
  const textPadY = 3;
  const fontSize = 9; // matches text-canvas-sm token (9px)
  const estimatedWidth = label.length * 5.5 + textPadX * 2;
  const estimatedHeight = fontSize + textPadY * 2;

  return (
    <g>
      <rect
        x={x - estimatedWidth / 2}
        y={y - estimatedHeight / 2}
        width={estimatedWidth}
        height={estimatedHeight}
        rx={estimatedHeight / 2}
        fill="var(--color-glass-fill-dense)"
        stroke="var(--color-glass-stroke-light)"
        strokeWidth={0.5}
      />
      <text
        x={x}
        y={y}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fontSize}
        fill="var(--color-muted)"
        fontFamily="inherit"
      >
        {label}
      </text>
    </g>
  );
}

// ---------------------------------------------------------------------------
// FlowRenderer — main component
// ---------------------------------------------------------------------------

export function FlowRenderer({
  steps,
  onApprove,
  onReject,
  onClick,
}: FlowRendererProps) {
  const [hoveredStepId, setHoveredStepId] = useState<string | null>(null);
  const leaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { positions, edges, totalWidth, totalHeight } =
    computeFlowLayout(steps);

  const positionMap = new Map<string, NodePosition>();
  for (const pos of positions) {
    positionMap.set(pos.stepId, pos);
  }

  const stepMap = new Map<string, FlowStep>();
  for (const step of steps) {
    stepMap.set(step.id, step);
  }

  // -- Hover handlers -------------------------------------------------------

  const handleNodeHover = useCallback((step: FlowStep) => {
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
    setHoveredStepId(step.id);
  }, []);

  const handleNodeLeave = useCallback(() => {
    leaveTimerRef.current = setTimeout(() => {
      setHoveredStepId(null);
      leaveTimerRef.current = null;
    }, HOVER_LEAVE_DELAY_MS);
  }, []);

  const handlePopoverEnter = useCallback(() => {
    if (leaveTimerRef.current !== null) {
      clearTimeout(leaveTimerRef.current);
      leaveTimerRef.current = null;
    }
  }, []);

  const handlePopoverLeave = useCallback(() => {
    setHoveredStepId(null);
  }, []);

  const handleNodeClick = useCallback(
    (step: FlowStep) => {
      onClick?.(step.id);
    },
    [onClick],
  );

  // -- Empty state ----------------------------------------------------------

  if (steps.length === 0 || positions.length === 0) {
    return (
      <div
        className="flex items-center justify-center py-6 text-canvas-md italic text-muted"
        data-testid="flow-renderer"
      >
        No steps to display
      </div>
    );
  }

  // -- Render ---------------------------------------------------------------

  const containerWidth = totalWidth + CONTAINER_PADDING * 2;
  const containerHeight = totalHeight + CONTAINER_PADDING * 2;

  const hoveredStep = hoveredStepId ? stepMap.get(hoveredStepId) : undefined;
  const hoveredPos = hoveredStepId
    ? positionMap.get(hoveredStepId)
    : undefined;

  return (
    <div
      className="relative mx-auto"
      style={{
        width: containerWidth,
        height: containerHeight,
        padding: CONTAINER_PADDING,
      }}
      data-testid="flow-renderer"
    >
      {/* SVG edge layer */}
      <div
        className="absolute"
        style={{
          left: CONTAINER_PADDING,
          top: CONTAINER_PADDING,
          width: totalWidth,
          height: totalHeight,
        }}
      >
        <EdgeLayer
          edges={edges}
          positions={positions}
          width={totalWidth}
          height={totalHeight}
        />
      </div>

      {/* Node layer */}
      <div
        className="relative"
        style={{ width: totalWidth, height: totalHeight }}
        data-testid="flow-node-layer"
      >
        {positions.map((pos) => {
          const step = stepMap.get(pos.stepId);
          if (!step) return null;

          return (
            <FlowNode
              key={pos.stepId}
              step={step}
              position={pos}
              onHover={handleNodeHover}
              onLeave={handleNodeLeave}
              onClick={handleNodeClick}
              onApprove={onApprove}
              onReject={onReject}
            />
          );
        })}
      </div>

      {/* Hover popover */}
      {hoveredStep && hoveredPos && (
        <div
          className="absolute"
          style={{
            left: CONTAINER_PADDING,
            top: CONTAINER_PADDING,
            width: totalWidth,
            height: totalHeight,
            pointerEvents: "none",
          }}
        >
          <StepPopover
            step={hoveredStep}
            anchorX={hoveredPos.x}
            anchorY={hoveredPos.y}
            anchorWidth={hoveredPos.width}
            anchorHeight={hoveredPos.height}
            containerHeight={totalHeight}
            onApprove={onApprove}
            onReject={onReject}
            onMouseEnter={handlePopoverEnter}
            onMouseLeave={handlePopoverLeave}
          />
        </div>
      )}
    </div>
  );
}
